import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import Reveal, { StaggerReveal } from "../components/motion/Reveal";
import MagneticButton from "../components/motion/MagneticButton";
import Tilt3D from "../components/motion/Tilt3D";
import StripeGradientBand, { DarkGradientBackdrop } from "../components/motion/StripeGradientBand";
import InspectionFilmstrip from "../components/InspectionFilmstrip";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import AICommandCenter from "../components/AICommandCenter";
import ReservationShowcase from "../components/ReservationShowcase";
import ClientPaymentBlock from "../components/ClientPaymentBlock";
import CountUp from "../components/motion/CountUp";
import {
  RevenueMockup, PaymentMockup, ClientsMockup, CalendarMockup,
  ContractMockup, SignatureMockup, InspectionMockup,
  HeroDashboardMockup,
} from "../components/BecomeHostMockups";
import {
  ArrowRight, Sparkles, FileText, Pencil, ClipboardCheck,
  Users, ShieldCheck, TrendingUp, Zap, Quote,
  CalendarClock, Edit3, RefreshCw, CheckCheck, Play, Gift, Check, ChevronLeft, ChevronRight,
  LifeBuoy, Clock, XCircle, Database, LayoutGrid, Globe, CreditCard, BarChart3,
  Package, Lock, Search, BookOpen, Smartphone,
} from "lucide-react";
import { getUseCase } from "../data/useCases";
import { BECOME_HOST_FAQ } from "../data/helpContent";
import { FaqRow } from "./HelpCenter";

// ─── Shared tokens (aligned with /app/design_guidelines.json) ────────────
const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";
const H2 = "font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-slate-900";
const H2_DARK = "font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-white";
const SUBTITLE = "text-lg md:text-xl text-slate-600 leading-relaxed";
const SUBTITLE_DARK = "text-lg md:text-xl text-slate-300 leading-relaxed";

// Narration "journée du loueur"
function DayChip({ time, label, dark }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold mb-5 ${
        dark ? "bg-white/10 border border-white/15 text-violet-200" : "bg-violet-50 border border-violet-100 text-violet-700"
      }`}
      style={{ animation: "pulseGlow 2.6s ease-in-out infinite" }}
      data-testid={`day-chip-${time.replace("h", "")}`}
    >
      <Clock className="h-3 w-3" /> {time}
      <span className={`font-medium ${dark ? "text-slate-300" : "text-slate-500"}`}>· {label}</span>
    </div>
  );
}

export default function BecomeHost() {
  const openDemo = openInteractiveDemo;
  return (
    <div className="min-h-screen bg-white overflow-x-clip">
      <PublicNavbar transparent />
      <HeroSection onDemo={openDemo} />
      <WhyJunago />
      <UniversesSection />
      <FleetCalendar />
      <BookingLifecycle />
      <LegalShowcase />
      <InspectionFilmstrip />
      <BookingSiteSection />
      <RevenueKpi />
      <DenseFeatures />
      <CaseStudyTeaser />
      <PricingTeaser onDemo={openDemo} />
      <ComingSoonSection />
      <TestimonialCarousel />
      <FaqSection />
      <BottomCta onDemo={openDemo} />
      <Footer />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1) HERO — center_cinematic
// ═══════════════════════════════════════════════════════════════════════
function HeroSection({ onDemo }) {
  const dashRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const onDashMove = (e) => {
    const r = dashRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -4, y: px * 5 });
  };
  const words1 = "La plateforme tout-en-un des loueurs de voitures.".split(" ");
  const words2 = "Du planning à l'encaissement.".split(" ");
  return (
    <section className="relative -mt-16 pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden" data-testid="hero-section">
      {/* Stripe-like animated pastel gradient band */}
      <StripeGradientBand />
      {/* Orbes décoratives flottantes */}
      <div aria-hidden className="absolute top-40 left-[8%] h-3 w-3 rounded-full bg-violet-400/50 blur-[1px] pointer-events-none hidden md:block" style={{ animation: "orbitFloat 9s ease-in-out infinite" }} />
      <div aria-hidden className="absolute top-64 right-[10%] h-2 w-2 rounded-full bg-fuchsia-400/60 pointer-events-none hidden md:block" style={{ animation: "orbitFloat 7s 1.5s ease-in-out infinite" }} />
      <div aria-hidden className="absolute top-96 left-[16%] h-2.5 w-2.5 rounded-full bg-indigo-400/40 pointer-events-none hidden md:block" style={{ animation: "orbitFloat 11s 0.8s ease-in-out infinite" }} />
      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-violet-600/10 via-fuchsia-500/10 to-violet-600/10 border border-violet-300/60 mb-6" data-testid="hero-ai-badge">
            <Sparkles className="h-3.5 w-3.5 text-violet-600 animate-pulse" style={{ animationDuration: "2s" }} />
            <span className="text-xs font-bold tracking-wide text-violet-700 uppercase">IA intégrée</span>
            <span className="px-1.5 py-0.5 rounded-full bg-violet-600 text-white text-[9px] font-bold">Junago Copilot</span>
          </div>
        </Reveal>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-slate-900 max-w-4xl mx-auto" data-testid="hero-title">
          {words1.map((w, i) => (
            <span key={i} className="hero-word" style={{ animationDelay: `${120 + i * 55}ms` }}>{w}&nbsp;</span>
          ))}
          <span className="whitespace-normal">
            {words2.map((w, i) => (
              <span key={i} className="hero-word text-gradient-animated" style={{ animationDelay: `${120 + (words1.length + i) * 55}ms` }}>{w}&nbsp;</span>
            ))}
          </span>
        </h1>
        <Reveal delay={0.2}>
          <p className={`${SUBTITLE} mt-6 max-w-3xl mx-auto`}>
            Gérez votre flotte, activez votre site internet de réservation, automatisez paiements, contrats,
            états des lieux et revenus — avec Junago Copilot, l&apos;IA intégrée, et votre équipe à bord. Le tout au même endroit.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/register?context=vendor"
              className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] hover:shadow-[0_20px_50px_-15px_rgba(124,58,237,0.6)]"
              data-testid="hero-start-free-btn"
            >
              Commencer <ArrowRight className="btn-arrow h-4 w-4" />
            </Link>
            <Link
              to="/register?context=vendor"
              className="btn-jn-secondary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-[1.04]"
              data-testid="hero-signup-btn"
            >
              S&apos;inscrire
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-500 inline-flex items-center gap-2 justify-center">
            <Gift className="h-3 w-3 text-violet-600" /> Essai gratuit 30 jours · Sans carte bancaire · Résiliable en 1 clic
          </p>
        </Reveal>

        {/* Grand tableau de bord + bouton Lancer la démo au centre */}
        <Reveal delay={0.3}>
          <div className="relative mt-16 md:mt-20 max-w-6xl mx-auto" style={{ perspective: "1400px" }}>
            {/* Glow ambiant derrière le dashboard */}
            <div aria-hidden className="absolute -inset-x-10 -inset-y-8 opacity-40 blur-3xl pointer-events-none" style={{
              background: "radial-gradient(ellipse at 25% 15%, rgba(124,58,237,0.35), transparent 55%), radial-gradient(ellipse at 80% 85%, rgba(217,70,239,0.2), transparent 50%)",
            }} />
            <div
              ref={dashRef}
              onMouseMove={onDashMove}
              onMouseLeave={() => setTilt({ x: 0, y: 0 })}
              className="relative rounded-3xl overflow-hidden ring-1 ring-slate-900/10 shadow-[0_60px_120px_-40px_rgba(15,23,42,0.5)]"
              style={{
                transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
                transformStyle: "preserve-3d",
              }}
              data-testid="hero-dashboard-tilt"
            >
              <HeroDashboardMockup />
              {/* Overlay central — Lancer la démo */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <button
                  onClick={onDemo}
                  className="pointer-events-auto group flex flex-col items-center"
                  data-testid="hero-launch-demo"
                >
                  <span className="btn-jn-play h-16 w-16 transition-transform group-hover:scale-105">
                    <Play className="h-6 w-6 fill-current" />
                  </span>
                  <span className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/95 backdrop-blur border border-slate-200/80 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.35)] text-sm font-semibold text-slate-900">
                    Lancer la démo
                  </span>
                  <span className="mt-2.5 px-3 py-1 rounded-full bg-slate-950/70 backdrop-blur text-[11px] font-medium text-white">
                    Vivez la journée d&apos;un loueur de voitures — 2 min, sans compte
                  </span>
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1.5) POURQUOI JUNAGO — avant / après
// ═══════════════════════════════════════════════════════════════════════
function WhyJunago() {
  const before = ["Excel", "Téléphone", "Agenda papier", "Contrats Word", "Paiements séparés", "Double saisie"];
  const after = [
    { icon: LayoutGrid, label: "Une seule plateforme" },
    { icon: Database, label: "Une seule base de données" },
    { icon: Sparkles, label: "Une seule IA" },
    { icon: ShieldCheck, label: "Une seule source de vérité" },
  ];
  return (
    <section className="bg-slate-50 py-24 md:py-32" data-testid="why-junago-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <Reveal>
            <div className={`${OVERLINE} mb-4`}>Pourquoi Junago</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className={H2}>Votre activité mérite mieux qu&apos;un tableur.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`${SUBTITLE} mt-4`}>
              La plupart des loueurs jonglent encore entre six outils qui ne se parlent pas. Junago les remplace tous.
            </p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-stretch relative">
          {/* Flèche de convergence entre les deux cartes */}
          <div aria-hidden className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-violet-600 text-white items-center justify-center shadow-[0_12px_32px_-8px_rgba(124,58,237,0.6)]" style={{ animation: "nodePulse 2.4s ease-in-out infinite" }}>
            <ArrowRight className="h-5 w-5" />
          </div>
          <Reveal>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 md:p-10 transition-shadow duration-300 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)]" data-testid="why-before-card">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">Avant Junago</div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {before.map((b, i) => (
                  <Reveal key={b} delay={150 + i * 110} y={14} as="span" className="inline-block">
                    <span
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 bg-slate-50 text-sm font-medium text-slate-500 transition-colors duration-300 hover:border-red-200 hover:bg-red-50 hover:text-red-500 hover:line-through cursor-default"
                      style={{ "--tilt": `${i % 2 === 0 ? -1.5 : 1.5}deg`, animation: `chipWobble ${5 + i * 0.7}s ease-in-out infinite` }}
                    >
                      <XCircle className="h-3.5 w-3.5 text-red-400" /> {b}
                    </span>
                  </Reveal>
                ))}
              </div>
              <p className="mt-8 text-sm text-slate-500 leading-relaxed">
                Six outils, six mots de passe, six versions de la vérité. L&apos;information se perd, les erreurs se
                multiplient, et chaque journée commence par une chasse à l&apos;information.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="sheen-card relative h-full rounded-3xl bg-slate-950 text-white p-8 md:p-10 overflow-hidden" data-testid="why-after-card">
              <div aria-hidden className="absolute -right-16 -bottom-24 h-72 w-72 rounded-full bg-violet-600 opacity-25 blur-3xl" />
              <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
              <div className="relative">
                <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-violet-300">Avec Junago</div>
                <div className="mt-6 space-y-3.5">
                  {after.map((a, i) => (
                    <Reveal key={a.label} delay={350 + i * 160} y={16}>
                      <div className="group flex items-center gap-3.5 rounded-2xl -mx-2 px-2 py-1 transition-colors duration-300 hover:bg-white/5">
                        <span className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:bg-violet-500/25 group-hover:border-violet-400/40">
                          <a.icon className="h-4 w-4 text-violet-300" />
                        </span>
                        <span className="font-heading text-lg font-medium tracking-tight">{a.label}</span>
                        <Check className="ml-auto h-4 w-4 text-emerald-400 opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" strokeWidth={3} />
                      </div>
                    </Reveal>
                  ))}
                </div>
                <p className="mt-8 text-sm text-slate-400 leading-relaxed">
                  Une information saisie une fois se retrouve partout : planning, contrat, facture, statistiques.
                  Et l&apos;IA veille sur l&apos;ensemble.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1.6) LES GRANDS UNIVERS — Gérer / Vendre / Encaisser / Piloter
// ═══════════════════════════════════════════════════════════════════════
function UniversesSection() {
  const universes = [
    {
      icon: LayoutGrid, title: "Gérer", to: "/solutions/gerer-son-activite",
      desc: "Toute l'exploitation quotidienne, centralisée.",
      chips: ["Calendrier", "Réservations", "Véhicules", "Clients", "Contrats", "États des lieux"],
    },
    {
      icon: Globe, title: "Vendre", to: "/solutions/vendre-en-ligne",
      desc: "Votre canal de réservation directe, ouvert 24h/24.",
      chips: ["Site internet de réservation", "Produits", "Options", "Disponibilités", "Tarification"],
    },
    {
      icon: CreditCard, title: "Encaisser", to: "/solutions/encaisser-et-securiser",
      desc: "L'argent rentre tout seul, chaque location est couverte.",
      chips: ["Paiements", "Stripe Connect", "Cautions", "Facturation", "Revenus"],
    },
    {
      icon: BarChart3, title: "Piloter", to: "/solutions/piloter-son-entreprise",
      desc: "Des décisions étayées, une équipe sous contrôle.",
      chips: ["Copilot", "Statistiques", "Rentabilité", "Équipe", "Permissions"],
    },
  ];
  return (
    <section className="bg-white py-24 md:py-32" data-testid="universes-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <Reveal>
            <div className={`${OVERLINE} mb-4`}>Les grands univers</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className={H2}>Quatre univers. Une seule plateforme.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`${SUBTITLE} mt-4`}>
              Gérer, vendre, encaisser, piloter : chaque univers regroupe les modules qui travaillent ensemble pour vous.
            </p>
          </Reveal>
        </div>
        <StaggerReveal className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" step={110} y={30}>
          {universes.map((u, i) => (
            <Link
              key={u.title}
              to={u.to}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
              }}
              className="universe-card group rounded-3xl border border-slate-200 bg-white p-7 hover:border-violet-300 hover:shadow-[0_24px_60px_-20px_rgba(124,58,237,0.4)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col overflow-hidden"
              data-testid={`universe-card-${u.title.toLowerCase()}`}
            >
              <span className="universe-glow" aria-hidden />
              <span aria-hidden className="absolute -top-3 right-4 font-heading text-[80px] font-bold text-slate-100 leading-none select-none transition-colors duration-300 group-hover:text-violet-100">0{i + 1}</span>
              <div className="relative h-11 w-11 rounded-xl bg-violet-50 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-[-6deg] group-hover:bg-violet-600 group-hover:shadow-[0_10px_24px_-8px_rgba(124,58,237,0.7)]">
                <u.icon className="h-5 w-5 text-violet-600 transition-colors duration-300 group-hover:text-white" />
              </div>
              <h3 className="relative mt-4 font-heading text-2xl font-medium tracking-tight text-slate-900 group-hover:text-violet-700 transition-colors">{u.title}</h3>
              <p className="relative mt-1.5 text-sm text-slate-600 leading-relaxed">{u.desc}</p>
              <div className="relative mt-4 flex flex-wrap gap-1.5">
                {u.chips.map((c, ci) => (
                  <span
                    key={c}
                    className="px-2.5 py-1 rounded-full bg-slate-100 text-[11px] font-medium text-slate-600 transition-all duration-300 group-hover:bg-violet-50 group-hover:text-violet-700"
                    style={{ transitionDelay: `${ci * 35}ms` }}
                  >
                    {c}
                  </span>
                ))}
              </div>
              {/* Bouton qui apparaît au survol */}
              <span className="relative mt-auto pt-6">
                <span
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold shadow-[0_12px_30px_-10px_rgba(124,58,237,0.7)] transition-all duration-300 md:opacity-0 md:translate-y-3 md:group-hover:opacity-100 md:group-hover:translate-y-0"
                  data-testid={`universe-btn-${u.title.toLowerCase()}`}
                >
                  Voir la solution <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </span>
            </Link>
          ))}
        </StaggerReveal>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 4.5) SITE INTERNET DE RÉSERVATION — captures réelles desktop + mobile
// ═══════════════════════════════════════════════════════════════════════
function BookingSiteSection() {
  const points = [
    { icon: Globe, label: "Hébergement & SSL inclus" },
    { icon: RefreshCw, label: "Disponibilités en temps réel" },
    { icon: Package, label: "Produits & options intégrés" },
    { icon: Lock, label: "Paiement en ligne sécurisé" },
    { icon: Search, label: "SEO optimisé" },
    { icon: Zap, label: "Synchronisation automatique" },
  ];
  return (
    <section className="relative bg-slate-950 py-24 md:py-32 overflow-hidden" data-testid="booking-site-section">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 15% 20%, rgba(124,58,237,0.25), transparent 55%), radial-gradient(ellipse at 90% 90%, rgba(217,70,239,0.15), transparent 50%)",
      }} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <DayChip time="23h04" label="Un client réserve pendant que vous dormez" dark />
            </Reveal>
            <Reveal>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-400 mb-4">Site internet de réservation</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className={H2_DARK}>Votre agence, réservable 24h/24.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className={`${SUBTITLE_DARK} mt-5`}>
                Junago génère et héberge votre site internet professionnel de réservation en ligne : design premium,
                disponibilités réelles, paiement sécurisé. Chaque modification dans votre logiciel est répercutée
                automatiquement — zéro maintenance, zéro double saisie.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {points.map((p) => (
                  <div key={p.label} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <span className="h-8 w-8 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                      <p.icon className="h-3.5 w-3.5 text-violet-300" />
                    </span>
                    {p.label}
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/solutions/vendre-en-ligne" className="btn-jn-light inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="booking-site-solution-link">
                  Découvrir la solution <ArrowRight className="btn-arrow h-4 w-4" />
                </Link>
                <Link to="/fonctionnalites/page-de-reservation" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="booking-site-feature-link">
                  La fonctionnalité en détail
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 relative">
            <Reveal delay={0.1}>
              <div className="relative pr-0 sm:pr-24">
                {/* Desktop — capture réelle */}
                <div className="rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_50px_100px_-30px_rgba(0,0,0,0.7)]" data-testid="booking-site-desktop-capture">
                  <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-900 border-b border-white/10">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span className="ml-3 text-[10px] font-mono text-slate-500 truncate">junago.io/p/votre-agence</span>
                  </div>
                  <img src="/captures/site_reservation.jpg" alt="Site internet de réservation généré par Junago — version desktop" className="w-full block" loading="lazy" />
                </div>
                {/* Mobile — capture réelle flottante */}
                <div
                  className="hidden sm:block absolute -bottom-10 right-0 w-44 rounded-[1.6rem] border-[5px] border-slate-800 overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] bg-slate-900"
                  style={{ animation: "floatSlow 7s ease-in-out infinite" }}
                  data-testid="booking-site-mobile-capture"
                >
                  <img src="/captures/site_mobile.jpg" alt="Site internet de réservation Junago — version mobile" className="w-full block" loading="lazy" />
                </div>
                <figcaption className="mt-5 flex items-center gap-2 text-xs text-slate-500">
                  <Smartphone className="h-3.5 w-3.5" /> Captures réelles d&apos;un site de réservation généré par Junago — desktop et mobile.
                </figcaption>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 5.8) CAS D'USAGE — teaser premium
// ═══════════════════════════════════════════════════════════════════════
function CaseStudyTeaser() {
  const cs = getUseCase("location-premium-gestion-centralisee");
  if (!cs) return null;
  return (
    <section className="bg-white py-24 md:py-32" data-testid="use-case-teaser-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <Reveal>
              <div className={`${OVERLINE} mb-4`}>Cas d&apos;usage</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className={H2}>Junago en situation.</h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link to="/cas-d-usage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700" data-testid="use-case-teaser-all">
              <BookOpen className="h-4 w-4" /> Tous les cas d&apos;usage
            </Link>
          </Reveal>
        </div>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl group" data-testid="use-case-teaser-card">
            <img src={cs.heroImg} alt={cs.heroAlt} className="w-full h-[460px] md:h-[520px] object-cover transition-transform duration-700 group-hover:scale-[1.02]" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/50 to-slate-950/10" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-violet-300">{cs.profile.label}</div>
                <blockquote className="mt-3 font-heading text-xl md:text-2xl lg:text-3xl font-medium tracking-tight leading-snug text-white max-w-2xl">
                  {cs.takeaway}
                </blockquote>
                <div className="mt-4 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center shrink-0">
                    <cs.profile.icon className="h-4.5 w-4.5 text-white" style={{ height: 18, width: 18 }} />
                  </span>
                  <div className="text-sm text-white/80">
                    <span className="font-semibold text-white">{cs.profile.label}</span> — {cs.profile.detail}
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {cs.solution.modules.slice(0, 5).map((m) => (
                    <Link key={m.label} to={m.to} className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] font-semibold text-white hover:bg-white/20 transition-colors">
                      {m.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to={`/cas-d-usage/${cs.slug}`}
                  className="btn-jn-light inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                  data-testid="use-case-teaser-cta"
                >
                  Voir le cas d&apos;usage complet <ArrowRight className="btn-arrow h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 2) LEGAL SHOWCASE — bento_grid (Contrats + Signatures + États des lieux)
// ═══════════════════════════════════════════════════════════════════════
function LegalShowcase() {
  return (
    <section className="bg-slate-50 py-24 md:py-32" data-testid="legal-showcase-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <Reveal>
            <DayChip time="10h45" label="Le contrat est prêt à signer" />
          </Reveal>
          <Reveal>
            <div className={`${OVERLINE} mb-4`}>Cadre juridique automatisé</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className={H2}>Zéro papier. Zéro stress juridique.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`${SUBTITLE} mt-4`}>
              Des contrats de location de voiture générés en un clic à l&apos;état des lieux photographique sécurisé. Votre logiciel automatise tout.
            </p>
          </Reveal>
        </div>

        {/* Bento 12-cols */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left large card — Contrats */}
          <div className="md:col-span-7">
            <Reveal>
              <Tilt3D>
                <BentoCard
                  overline="Contrats automatiques"
                  title="Générés en 1 clic à chaque réservation."
                  desc="Modèle aux normes, personnalisable, envoyé par email et signable sur mobile en moins de 60 secondes."
                  icon={FileText}
                  featured
                  testid="legal-contracts"
                >
                  <div className="mt-6 rounded-xl overflow-hidden ring-1 ring-black/5 shadow-lg">
                    <ContractMockup />
                  </div>
                </BentoCard>
              </Tilt3D>
            </Reveal>
          </div>

          {/* Right column stacked */}
          <div className="md:col-span-5 grid grid-cols-1 gap-6">
            <Reveal delay={0.1}>
              <Tilt3D>
                <BentoCard
                  overline="Signature mobile"
                  title="Validée sur smartphone."
                  desc="Le client signe avec le doigt avant la remise des clés. Horodaté, IP tracée, opposable en justice."
                  icon={Pencil}
                  testid="legal-signatures"
                >
                  <div className="mt-4 rounded-xl overflow-hidden ring-1 ring-black/5">
                    <SignatureMockup />
                  </div>
                </BentoCard>
              </Tilt3D>
            </Reveal>
            <Reveal delay={0.15}>
              <Tilt3D>
                <BentoCard
                  overline="État des lieux digital"
                  title="4 étapes, photos par zone."
                  desc="Kilométrage, dommages, checklist, signature. Chaque étape horodatée avec géolocalisation."
                  icon={ClipboardCheck}
                  testid="legal-inspection"
                >
                  <div className="mt-4 rounded-xl overflow-hidden ring-1 ring-black/5">
                    <InspectionMockup />
                  </div>
                </BentoCard>
              </Tilt3D>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function BentoCard({ overline, title, desc, icon: Icon, children, featured, testid }) {
  return (
    <div
      className={`h-full rounded-3xl bg-white border border-slate-200/70 p-6 md:p-8 transition-shadow hover:shadow-xl ${featured ? "md:min-h-[520px]" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-violet-50 flex items-center justify-center">
          <Icon className="h-4 w-4 text-violet-600" />
        </div>
        <span className={OVERLINE}>{overline}</span>
      </div>
      <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-slate-900 leading-tight">{title}</h3>
      <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed">{desc}</p>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 3) FLEET CALENDAR — split_asymmetric_dark (with overflow mockup)
// ═══════════════════════════════════════════════════════════════════════
function PlanningShowcase() {
  const days = ["Lun 8", "Mar 9", "Mer 10", "Jeu 11", "Ven 12", "Sam 13", "Dim 14"];
  const rows = [
    { name: "Tesla Model 3", bars: [
      { l: 8, w: 34, cls: "bg-violet-600", label: "S. Laurent", light: true, client: "Sophie Laurent", initials: "SL", vehicle: "Tesla Model 3 · AA-123-BB", dates: "Lun 8 → Ven 12 · 4 jours", price: "516 €", paid: "Payé", status: "En cours", statusCls: "bg-emerald-100 text-emerald-700" },
      { l: 64, w: 24, cls: "bg-slate-200", label: "T. Morel", client: "Thomas Morel", initials: "TM", vehicle: "Tesla Model 3 · AA-123-BB", dates: "Sam 13 → Dim 14 · 2 jours", price: "258 €", paid: "Acompte", status: "À venir", statusCls: "bg-violet-100 text-violet-700" },
    ] },
    { name: "Mercedes GLC", bars: [
      { l: 0, w: 20, cls: "bg-slate-200", label: "A. Petit", client: "Alice Petit", initials: "AP", vehicle: "Mercedes GLC · BB-456-CC", dates: "Lun 8 → Mar 9 · 2 jours", price: "398 €", paid: "Payé", status: "Terminée", statusCls: "bg-slate-100 text-slate-600" },
      { l: 42, w: 28, cls: "bg-violet-200", label: "M. Diallo", client: "Mamadou Diallo", initials: "MD", vehicle: "Mercedes GLC · BB-456-CC", dates: "Mer 10 → Ven 12 · 3 jours", price: "597 €", paid: "Acompte", status: "À venir", statusCls: "bg-violet-100 text-violet-700" },
    ] },
    { name: "BMW Série 5", bars: [
      { l: 24, w: 38, cls: "bg-violet-300", label: "C. Robin", client: "Claire Robin", initials: "CR", vehicle: "BMW Série 5 · CC-789-DD", dates: "Mar 9 → Ven 12 · 4 jours", price: "756 €", paid: "Payé", status: "En cours", statusCls: "bg-emerald-100 text-emerald-700" },
    ] },
    { name: "Peugeot 208", bars: [
      { l: 52, w: 34, cls: "bg-slate-200", label: "L. Girard", client: "Lucas Girard", initials: "LG", vehicle: "Peugeot 208 · DD-012-EE", dates: "Jeu 11 → Sam 13 · 3 jours", price: "147 €", paid: "En attente", status: "À venir", statusCls: "bg-violet-100 text-violet-700" },
    ] },
  ];
  return (
    <div className="mockup-frame w-full" data-testid="planning-showcase">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="relative p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading text-sm font-semibold text-slate-900">Planning · Semaine 24</div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 border border-violet-100 text-[10px] font-bold text-violet-700">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500 animate-pulse" /> Temps réel
        </span>
      </div>
      <div className="grid grid-cols-[88px_1fr] gap-3 mb-2">
        <div />
        <div className="grid grid-cols-7 gap-1 text-center">
          {days.map((d, i) => (
            <div key={d} className={`text-[10px] font-semibold py-1 rounded-md ${i === 2 ? "bg-violet-600 text-white" : "text-slate-400"}`}>{d}</div>
          ))}
        </div>
      </div>
      <div className="space-y-2.5">
        {rows.map((r, rowIdx) => (
          <div key={r.name} className="grid grid-cols-[88px_1fr] gap-3 items-center">
            <div className="text-[11px] font-medium text-slate-700 truncate">{r.name}</div>
            <div className="relative h-9 rounded-lg bg-slate-50 border border-slate-100">
              <div aria-hidden className="absolute inset-y-0 bg-violet-50/80" style={{ left: "28.57%", width: "14.28%" }} />
              {r.bars.map((b) => (
                <div
                  key={b.label}
                  className={`group/bar absolute top-1 bottom-1 rounded-md ${b.cls} flex items-center px-2 cursor-pointer transition-shadow hover:ring-2 hover:ring-violet-400/60`}
                  style={{ left: `${b.l}%`, width: `${b.w}%` }}
                  data-testid={`resa-bar-${b.initials}`}
                >
                  <span className={`text-[9px] font-semibold truncate ${b.light ? "text-white" : "text-slate-700"}`}>{b.label}</span>
                  {/* Vignette de la résa au survol */}
                  <div
                    className={`hidden group-hover/bar:block absolute ${rowIdx < 2 ? "top-full mt-2" : "bottom-full mb-2"} ${b.l > 42 ? "right-0" : "left-0"} w-60 rounded-2xl bg-white border border-slate-200 shadow-2xl p-3.5 z-30 cursor-default`}
                    style={{ animation: "fadeInUp 150ms ease-out both" }}
                    data-testid="resa-popup"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="h-8 w-8 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center shrink-0">{b.initials}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{b.client}</div>
                        <div className="text-[10px] text-slate-500 truncate">{b.vehicle}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold shrink-0 ${b.statusCls}`}>{b.status}</span>
                    </div>
                    <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[10px]">
                      <span className="text-slate-500">{b.dates}</span>
                      <span className="font-bold text-slate-900">{b.price} <span className="text-emerald-600 font-semibold">{b.paid}</span></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

function FleetCalendar() {
  const ringRef = React.useRef(null);
  const [ringVisible, setRingVisible] = useState(false);
  useEffect(() => {
    const el = ringRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setRingVisible(true); }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const bullets = [
    { icon: CalendarClock, title: "Zéro double réservation", desc: "Les conflits sont bloqués à la source, automatiquement." },
    { icon: Zap, title: "Disponibilités en temps réel", desc: "Chaque changement est répercuté instantanément sur le planning." },
    { icon: TrendingUp, title: "Rotation optimisée", desc: "Repérez les creux du planning et remplissez-les." },
  ];
  return (
    <section className="relative bg-white py-24 md:py-32 overflow-hidden" data-testid="fleet-calendar-section">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 85% 10%, rgba(124,58,237,0.08) 0%, transparent 50%), radial-gradient(ellipse at 5% 90%, rgba(124,58,237,0.05) 0%, transparent 45%)",
      }} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-center">
          {/* Left text */}
          <div className="lg:col-span-5">
            <Reveal>
              <DayChip time="08h00" label="Vous ouvrez votre planning" />
            </Reveal>
            <Reveal>
              <div className={`${OVERLINE} mb-4`}>Pilotage en temps réel</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className={H2}>Une vision claire sur toute votre flotte.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className={`${SUBTITLE} mt-5`}>
                Un calendrier Gantt ultra-réactif, au cœur de votre logiciel de gestion de flotte : zéro double réservation, rotation de vos véhicules de location optimisée.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 space-y-5">
                {bullets.map((b) => (
                  <div key={b.title} className="flex items-start gap-4">
                    <span className="h-10 w-10 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
                      <b.icon className="h-[18px] w-[18px] text-violet-600" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{b.title}</div>
                      <div className="text-sm text-slate-600 mt-0.5 leading-relaxed">{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/register?context=vendor"
                className="btn-primary mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
                data-testid="fleet-cta"
              >
                Découvrir le pilotage <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          {/* Right — planning animé avec curseur et popup */}
          <div className="lg:col-span-7 relative">
            <Reveal delay={0.1}>
              <div className="relative pt-6">
                <PlanningShowcase />
                {/* Carte flottante — taux d'occupation (verre dépoli) */}
                <div
                  ref={ringRef}
                  className="hidden md:flex absolute -top-4 -right-4 items-center gap-3 rounded-2xl bg-white/55 backdrop-blur-xl border border-white/70 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)]"
                  style={{ animation: "floatSlow 6s ease-in-out infinite" }}
                  data-testid="fleet-occupancy-card"
                >
                  <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden>
                    <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(15,23,42,0.1)" strokeWidth="5" />
                    <circle cx="22" cy="22" r="18" fill="none" stroke="#7c3aed" strokeWidth="5" strokeLinecap="round" strokeDasharray="113" strokeDashoffset="113" className={ringVisible ? "ring-fill" : ""} transform="rotate(-90 22 22)" />
                  </svg>
                  <div>
                    <div className="font-heading text-lg font-semibold text-slate-900 leading-none">72 %</div>
                    <div className="text-[10px] text-slate-600 mt-1">Taux d&apos;occupation</div>
                  </div>
                </div>
                {/* Carte flottante — zéro conflit (verre dépoli) */}
                <div
                  className="hidden md:flex absolute -bottom-6 -left-4 items-center gap-2.5 rounded-2xl bg-white/55 backdrop-blur-xl border border-white/70 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)]"
                  style={{ animation: "floatSlow 7s 1.2s ease-in-out infinite" }}
                  data-testid="fleet-conflict-card"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                  <div className="text-xs text-slate-800 font-medium">0 conflit de réservation ce mois-ci</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 4) REVENUE KPI — split_asymmetric_light with big number
// ═══════════════════════════════════════════════════════════════════════
function RevenueKpi() {
  return (
    <section className="bg-white py-24 md:py-32" data-testid="revenue-kpi-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left mockup floating */}
          <div className="lg:col-span-7 relative order-2 lg:order-1">
            <Reveal>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                <RevenueMockup />
              </div>
              {/* floating chip */}
              <div className="hidden md:block absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-4">
                <div className="text-xs text-slate-500">Encaissements</div>
                <div className="font-heading text-2xl font-semibold text-slate-900">100 % automatisés</div>
              </div>
            </Reveal>
          </div>
          {/* Right KPI */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal>
              <div className={`${OVERLINE} mb-4`}>Rentabilité</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className={H2}>Suivez vos revenus au centime près.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className={`${SUBTITLE} mt-4`}>
                Analysez la rentabilité de chaque véhicule de location, suivez le taux d&apos;occupation de votre flotte, visualisez vos encaissements en temps réel — et exportez vos écritures comptables en un clic.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 pt-8 border-t border-slate-100">
                <div className="font-heading text-7xl md:text-8xl font-bold tracking-tighter text-violet-600 leading-none">
                  <CountUp to={100} duration={1200} />&nbsp;%
                </div>
                <p className="mt-3 text-sm text-slate-600 max-w-xs">
                  De votre activité — encaissements, factures, cautions, dépenses, rentabilité par véhicule — visible au même endroit, en temps réel.
                </p>
                <Link to="/solutions/piloter-son-entreprise" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700" data-testid="revenue-solution-link">
                  Piloter son entreprise avec Junago <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 5) DENSE FEATURES — IA Command Center + Réservations + Client→Paiement
// ═══════════════════════════════════════════════════════════════════════
function DenseFeatures() {
  return (
    <section className="bg-slate-50 py-24 md:py-32" data-testid="dense-features-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-16">
          <Reveal>
            <div className={`${OVERLINE} mb-4`}>Nativement inclus</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className={H2}>Tout ce dont vous avez besoin, sans plugin.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`${SUBTITLE} mt-4`}>
              IA, réservations, CRM, paiements, produits & options, facturation, équipe : tout est nativement intégré.{" "}
              <Link to="/fonctionnalites" className="font-semibold text-violet-600 hover:text-violet-700" data-testid="dense-features-all-link">
                Explorer toutes les fonctionnalités →
              </Link>
            </p>
          </Reveal>
        </div>

        <div className="space-y-24 md:space-y-32">
          <Reveal>
            <AICommandCenter />
          </Reveal>
          <ReservationShowcase />
          <Reveal>
            <ClientPaymentBlock />
          </Reveal>
        </div>

        {/* Mini stats strip */}
        <div className="mt-24 pt-12 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-8">
          <MiniStat icon={ShieldCheck} value="99.9%" label="Uptime infrastructure" />
          <MiniStat icon={Zap} value="< 60s" label="Contrat généré" />
          <MiniStat icon={Gift} value="30 j" label="Essai gratuit" />
          <MiniStat icon={LifeBuoy} value="6j/7" label="Assistance en français" />
        </div>
      </div>
    </section>
  );
}

function MiniStat({ icon: Icon, value, label }) {
  return (
    <Reveal>
      <div className="flex flex-col">
        <Icon className="h-4 w-4 text-violet-600 mb-3" />
        <div className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{value}</div>
        <div className="mt-1 text-xs text-slate-500 leading-relaxed">{label}</div>
      </div>
    </Reveal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// COMING SOON — chronologie simplifiée + lien vers la roadmap complète
// ═══════════════════════════════════════════════════════════════════════
function ComingSoonSection() {
  const phases = [
    {
      year: "Aujourd'hui",
      dot: "bg-emerald-500",
      items: ["Junago — le logiciel complet", "Votre site internet de réservation en ligne", "Junago Copilot — l'IA qui vous assiste"],
    },
    {
      year: "2027",
      dot: "bg-violet-600",
      items: ["Nom de domaine personnalisé", "Place de marché Junago", "Commandes vocales du Copilot", "Notifications proactives"],
    },
    {
      year: "2028",
      dot: "bg-fuchsia-600",
      items: ["Agent IA autonome — l'IA agit à votre place, vous validez", "Pricing dynamique IA"],
    },
  ];
  return (
    <section className="bg-white py-16 md:py-20 border-t border-slate-100" data-testid="coming-soon-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <div className={OVERLINE}>Roadmap</div>
          <span className="text-[10px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
            Ce qui arrive
          </span>
          <a
            href="/assistance/roadmap"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700 transition-colors"
            data-testid="roadmap-full-link"
          >
            Voir la roadmap complète <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
        {/* Chronologie horizontale simplifiée */}
        <div className="relative">
          <div aria-hidden className="hidden md:block absolute top-[9px] left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-400 via-violet-500 to-fuchsia-500 rounded-full" />
          <div className="grid md:grid-cols-3 gap-8 md:gap-6">
            {phases.map((p) => (
              <div key={p.year} className="relative md:pt-8" data-testid={`roadmap-phase-${p.year}`}>
                <span aria-hidden className={`hidden md:block absolute top-[3px] left-0 h-3.5 w-3.5 rounded-full ${p.dot} ring-4 ring-white shadow`} />
                <div className="flex items-center gap-2.5 md:block">
                  <span aria-hidden className={`md:hidden h-3 w-3 rounded-full ${p.dot} shrink-0`} />
                  <div className="font-heading text-xl font-semibold text-slate-900">{p.year}</div>
                </div>
                <ul className="mt-3 space-y-2">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                      <Check className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" strokeWidth={3} /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 6) TESTIMONIAL — carousel 3 témoignages
// ═══════════════════════════════════════════════════════════════════════
const TESTIMONIALS = [
  {
    quote: "J'ai remplacé trois outils et un classeur papier par Junago. Tout est au même endroit, mon équipe s'y est mise en une journée.",
    highlight: "une journée",
    author: "Marc D.",
    role: "Paris Luxury Cars · Location premium",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/75e45bf202ffc88ea6aa8711f3d76e5e9405e816c10972d28e60082cd269ccbd.png",
  },
  {
    quote: "J'ai activé mon site de réservation un lundi matin, j'avais ma première réservation en ligne le soir même.",
    highlight: "le soir même",
    author: "Sonia B.",
    role: "Loueuse indépendante · Guadeloupe",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/42adef955c15a662f1bb1f30e9b8b07a772aed60b429d091287c43f9a47802bb.png",
  },
  {
    quote: "Le paiement par carte a fait disparaître les impayés. Et le virement arrive sans que j'y pense.",
    highlight: "sans que j'y pense",
    author: "David P.",
    role: "Loueur de vans aménagés · Biarritz",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/84a0a304f44dcd0e38b6d42112336c9b97118c62db3b125b118a5de118875861.png",
  },
];

function TestimonialCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 7000);
    return () => clearInterval(t);
  }, [paused]);
  const t = TESTIMONIALS[idx];
  const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
  const renderQuote = (q, hi) => {
    const parts = q.split(hi);
    return (
      <>
        {parts[0]}
        <span className="text-violet-600">{hi}</span>
        {parts.slice(1).join(hi)}
      </>
    );
  };
  return (
    <section
      className="bg-white py-32 md:py-40"
      data-testid="testimonial-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <Quote className="h-10 w-10 text-violet-600 mx-auto opacity-70" strokeWidth={1.5} />
        </Reveal>
        <div className="relative min-h-[280px] md:min-h-[340px] mt-8">
          {TESTIMONIALS.map((it, i) => (
            <div
              key={it.author}
              className={`absolute inset-0 transition-all duration-700 ${i === idx ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
              aria-hidden={i !== idx}
            >
              <blockquote className="font-heading text-3xl md:text-4xl lg:text-5xl italic tracking-tight leading-[1.15] text-slate-900">
                «&nbsp;{renderQuote(it.quote, it.highlight)}&nbsp;»
              </blockquote>
              <div className="mt-10 flex items-center justify-center gap-3">
                <img src={it.img} alt={it.author} className="h-12 w-12 rounded-full object-cover border-2 border-violet-100" loading="lazy" />
                <div className="text-left">
                  <div className="text-sm font-semibold text-slate-900">{it.author}</div>
                  <div className="text-xs text-slate-500">{it.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Controls */}
        <div className="mt-14 flex items-center justify-center gap-4">
          <button
            onClick={prev}
            aria-label="Témoignage précédent"
            className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center hover:border-slate-900 transition-colors"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="h-4 w-4 text-slate-700" />
          </button>
          <div className="flex items-center gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Témoignage ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-violet-600" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={next}
            aria-label="Témoignage suivant"
            className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center hover:border-slate-900 transition-colors"
            data-testid="testimonial-next"
          >
            <ChevronRight className="h-4 w-4 text-slate-700" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 3.5) BOOKING LIFECYCLE — horizontal step flow (NEW ergonomy)
// ═══════════════════════════════════════════════════════════════════════
function BookingLifecycle() {
  const steps = [
    {
      icon: CalendarClock, title: "Réception",
      desc: "Demandes qualifiées automatiquement, avec toutes les infos client + véhicule.",
      accent: "bg-violet-50 text-violet-600 border-violet-200",
      detailTitle: "Le client et la réservation, créés en 30 secondes.",
      detail: "Un appel, un email, un client au comptoir ? Renseignez le nom, les dates et le véhicule : Junago crée la fiche client et la réservation en même temps, et vérifie automatiquement la disponibilité.",
      points: [
        "Fiche client complète : permis, coordonnées, historique de location",
        "Disponibilité du véhicule vérifiée en temps réel",
        "Devis envoyé en un clic si le client hésite encore",
      ],
      Mockup: ClientsMockup,
    },
    {
      icon: CheckCheck, title: "Confirmation",
      desc: "Validez en un clic. Le contrat + la fiche client sont générés en même temps.",
      accent: "bg-emerald-50 text-emerald-600 border-emerald-200",
      detailTitle: "Un clic, et tout est prêt.",
      detail: "À la confirmation, le contrat de location est généré automatiquement avec les informations du client et du véhicule, prêt à être signé électroniquement. Le paiement est demandé selon vos conditions.",
      points: [
        "Contrat pré-rempli généré instantanément",
        "Signature électronique sur mobile ou tablette",
        "Acompte ou paiement complet, selon vos règles",
      ],
      Mockup: ContractMockup,
    },
    {
      icon: Edit3, title: "Modifications",
      desc: "Prolonger, changer de véhicule, ajouter un conducteur — sans refaire le contrat.",
      accent: "bg-sky-50 text-sky-600 border-sky-200",
      detailTitle: "La vie réelle change, votre planning suit.",
      detail: "Prolongation, changement de véhicule, conducteur additionnel : modifiez la réservation directement dans le calendrier. Le contrat est mis à jour par avenant, sans repartir de zéro.",
      points: [
        "Modification directe dans le calendrier Gantt",
        "Avenant au contrat généré automatiquement",
        "Le client est notifié à chaque changement",
      ],
      Mockup: CalendarMockup,
    },
    {
      icon: RefreshCw, title: "Clôture",
      desc: "État des lieux retour, facture, paiement libéré, avis client — tout enchaîné.",
      accent: "bg-amber-50 text-amber-600 border-amber-200",
      detailTitle: "Le retour du véhicule, sans friction.",
      detail: "Au retour : l'état des lieux est comparé automatiquement à celui du départ, la facture est émise, la caution libérée ou retenue avec justificatifs. Le véhicule redevient disponible dans la seconde.",
      points: [
        "Comparaison photo départ / retour automatique",
        "Facture et solde générés instantanément",
        "Véhicule remis en location immédiatement",
      ],
      Mockup: PaymentMockup,
    },
  ];
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const sectionRef = React.useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setActive((a) => (a + 1) % 4), 8000);
    return () => clearInterval(id);
  }, [inView, active]);
  const step = steps[active];
  const ActiveMockup = step.Mockup;
  return (
    <section ref={sectionRef} className="bg-white py-24 md:py-32" data-testid="booking-lifecycle-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <Reveal>
            <DayChip time="09h30" label="Une demande de location arrive" />
          </Reveal>
          <Reveal>
            <div className={`${OVERLINE} mb-4`}>Gestion des réservations</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className={H2}>Un cycle complet, du premier clic à la clôture.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`${SUBTITLE} mt-4`}>
              Recevez, confirmez, modifiez, prolongez, annulez, clôturez. Le cycle complet d&apos;une location de voiture, piloté depuis la plateforme. Cliquez sur chaque étape pour voir comment elle fonctionne.
            </p>
          </Reveal>
        </div>

        {/* Onglets cliquables avec rail chronologique */}
        <div className="relative">
          <div className="hidden md:block absolute top-9 left-8 right-8 h-px bg-gradient-to-r from-violet-200 via-slate-200 to-slate-200" aria-hidden />
          <StaggerReveal className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 relative">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === active;
              return (
                <button
                  key={s.title}
                  onClick={() => setActive(i)}
                  className={`relative z-10 text-left p-4 md:p-5 rounded-2xl border transition-colors ${
                    isActive ? "border-violet-300 bg-violet-50/70" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid={`lifecycle-tab-${i}`}
                  aria-selected={isActive}
                >
                  <div className="flex items-center gap-3">
                    <span className={`relative h-11 w-11 rounded-xl border ${s.accent} flex items-center justify-center shrink-0`}>
                      <Icon className="h-[18px] w-[18px]" />
                      <span className={`absolute -top-2 -right-2 h-5 w-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${isActive ? "bg-violet-600" : "bg-slate-900"}`}>{i + 1}</span>
                    </span>
                    <span className={`font-heading text-sm md:text-base font-semibold tracking-tight ${isActive ? "text-violet-900" : "text-slate-900"}`}>{s.title}</span>
                  </div>
                  <p className="mt-2.5 text-xs text-slate-500 leading-relaxed hidden md:block">{s.desc}</p>
                  {/* Barre de progression vers l'étape suivante */}
                  <div className="mt-3 h-[2px] rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                    {isActive && inView && (
                      <div
                        key={`progress-${active}`}
                        className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-400 rounded-full"
                        style={{ animation: "jnProgress 8000ms linear both" }}
                        data-testid={`lifecycle-progress-${i}`}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </StaggerReveal>
        </div>

        {/* Panneau de l'étape active */}
        <div
          key={active}
          className="mt-8 rounded-3xl border border-slate-200 bg-slate-50/70 p-6 md:p-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center"
          style={{ animation: "fadeInUp 400ms cubic-bezier(0.22,1,0.36,1) both" }}
          data-testid="lifecycle-panel"
        >
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-violet-600">Étape {active + 1} / 4 · {step.title}</div>
            <h3 className="mt-3 font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">{step.detailTitle}</h3>
            <p className="mt-4 text-slate-600 leading-relaxed">{step.detail}</p>
            <ul className="mt-6 space-y-3">
              {step.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-violet-700" strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white">
              <ActiveMockup />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 5.5) PRICING TEASER — offre commerciale (essai gratuit 30j)
// ═══════════════════════════════════════════════════════════════════════
function PricingTeaser({ onDemo }) {
  const perks = [
    "Véhicules illimités",
    "Contrats & états des lieux illimités",
    "Signature électronique incluse",
    "Support prioritaire 6j/7",
    "CRM clients & prospects inclus",
    "Résiliable à tout moment",
  ];
  return (
    <section className="relative bg-slate-50 py-24 md:py-32 overflow-hidden" data-testid="pricing-teaser-section">
      <div aria-hidden className="absolute -top-24 right-1/4 h-64 w-64 rounded-full bg-violet-200 opacity-40 blur-3xl" />
      <div className="relative max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          {/* Left — hero offer card */}
          <div className="lg:col-span-3 rounded-3xl bg-slate-950 text-white p-8 md:p-12 relative overflow-hidden">
            <div aria-hidden className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-violet-600 opacity-30 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/20 text-violet-200 text-[11px] font-bold tracking-[0.15em] uppercase">
                <Gift className="h-3 w-3" /> Offre de lancement
              </div>
              <h2 className="mt-5 font-heading text-4xl md:text-5xl font-medium tracking-tight leading-tight">
                <span className="text-white">1 mois d&apos;essai.</span>
                <br />
                <span className="text-violet-400">Gratuit.</span>
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Testez toutes les fonctionnalités pro du logiciel de location sans limite pendant 30 jours. Sans carte bancaire. Sans engagement. Vous décidez ensuite si Junago accompagne la croissance de votre flotte.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <MagneticButton>
                  <Link
                    to="/register?context=vendor"
                    className="btn-jn-light inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
                    data-testid="pricing-start-trial"
                  >
                    Commencer mon essai gratuit <ArrowRight className="btn-arrow h-4 w-4" />
                  </Link>
                </MagneticButton>
                <button onClick={onDemo} className="btn-jn-play-wrap" data-testid="pricing-see-demo">
                  <span className="btn-jn-play btn-jn-play--light"><Play className="h-4 w-4 fill-current" /></span>
                  <span className="btn-jn-play-label btn-jn-play-label--light">Voir la démo</span>
                </button>
              </div>
              <p className="mt-4 text-xs text-slate-400">
                Aucune carte requise · Résiliable en 1 clic · Assistance en français ·{" "}
                <Link to="/tarifs" className="underline hover:text-white" data-testid="pricing-see-plans">
                  Voir les tarifs
                </Link>
              </p>
            </div>
          </div>

          {/* Right — feature list */}
          <div className="lg:col-span-2">
            <div className={`${OVERLINE} mb-4`}>Inclus dans l&apos;essai</div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight leading-tight">
              L&apos;intégralité de la plateforme, sans limite.
            </h3>
            <ul className="mt-6 space-y-3">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-700" data-testid="pricing-perk">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-violet-600" strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FAQ — questions fréquentes des loueurs
// ═══════════════════════════════════════════════════════════════════════
function FaqSection() {
  return (
    <section className="bg-white py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left — title + assistance link */}
          <div className="lg:col-span-2">
            <div className={`${OVERLINE} mb-4`}>FAQ</div>
            <h2 className={H2}>
              Vos questions,
              <br />
              nos réponses.
            </h2>
            <p className={`${SUBTITLE} mt-4`}>
              Tout ce que les loueurs de voitures nous demandent avant d&apos;adopter le logiciel.
            </p>
            <Link
              to="/assistance"
              className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 hover:border-violet-300 hover:bg-violet-50/50 transition-all group"
              data-testid="faq-help-center-link"
            >
              <span className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                <LifeBuoy className="h-5 w-5 text-violet-600" />
              </span>
              <span className="text-left">
                <span className="block text-sm font-semibold text-slate-900">Centre d'assistance</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Guides détaillés et FAQ complète
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-violet-600 ml-auto transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden shadow-[0_8px_30px_-12px_rgba(15,23,42,0.08)]">
              {BECOME_HOST_FAQ.map((item) => (
                <FaqRow key={item.q} item={item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 7) BOTTOM CTA — immersive_cta
// ═══════════════════════════════════════════════════════════════════════
function BottomCta({ onDemo }) {
  return (
    <section className="relative bg-violet-900 py-24 md:py-32 overflow-hidden" data-testid="bottom-cta-section">
      {/* Animated gradient mesh (dark Stripe-like) */}
      <DarkGradientBackdrop />
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <svg className="absolute inset-0 h-full w-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-tight text-white">
            Prêt à accélérer votre croissance&nbsp;?
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg md:text-xl text-violet-100 leading-relaxed">
            Gestion complète, site internet de réservation, paiements automatisés, IA intégrée : tout Junago, gratuit pendant 30 jours.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
            {["Site de réservation inclus", "Junago Copilot (IA)", "Paiements Stripe", "Contrats & états des lieux", "Équipe & permissions"].map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold text-white" data-testid="cta-chip">
                <Check className="h-3 w-3 text-violet-300" strokeWidth={3} /> {c}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <MagneticButton>
              <Link
                to="/register?context=vendor"
                className="btn-jn-light inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold"
                data-testid="cta-start-free-btn"
              >
                Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
            </MagneticButton>
            <button onClick={onDemo} className="btn-jn-play-wrap" data-testid="cta-demo-btn">
              <span className="btn-jn-play btn-jn-play--light"><Play className="h-4 w-4 fill-current" /></span>
              <span className="btn-jn-play-label btn-jn-play-label--light">Voir la démo interactive</span>
            </button>
          </div>
          <p className="mt-6 text-xs text-violet-200">Essai gratuit 30 jours · Aucune carte requise · Setup en 5 minutes</p>
        </Reveal>
      </div>
    </section>
  );
}
